import type { InternalAxiosRequestConfig } from 'axios';

/**
 * How the subaccount selection is attached to a matching request:
 *
 * - `query`      → `?subaccount_number=N` merged into `config.params`
 * - `body`       → `subaccount_number: N` merged into the JSON body
 * - `batchBody`  → per-entry `subaccount: N` merged into `config.data.orders[]`
 *                  (note the field name, intentionally not `subaccount_number`)
 */
type InjectStrategy = 'query' | 'body' | 'batchBody';

type HttpMethod = 'GET' | 'POST' | 'DELETE';

interface SubaccountRoute {
    method: HttpMethod;
    /**
     * Path pattern, matched against the request pathname after stripping
     * any `/trade-api/v1` or `/v1` prefix. `*` matches a single path segment.
     */
    path: string;
    inject: InjectStrategy;
}

/**
 * v1 endpoints that gained subaccount scoping, per the backend spec.
 *
 * Order matters: more specific patterns must come before wildcard siblings.
 * The first entry whose method + path matches wins. To add a new endpoint,
 * append a row below.
 */
const SUBACCOUNT_ROUTES: readonly SubaccountRoute[] = [
    { method: 'GET', path: '/users/*/balance', inject: 'query' },
    { method: 'GET', path: '/users/*/portfolio/current_value', inject: 'query' },
    { method: 'GET', path: '/users/*/orders', inject: 'query' },
    { method: 'GET', path: '/users/*/order_history', inject: 'query' },
    { method: 'GET', path: '/users/*/trades', inject: 'query' },
    { method: 'GET', path: '/users/*/trade_history', inject: 'query' },
    { method: 'GET', path: '/users/*/positions', inject: 'query' },
    { method: 'GET', path: '/users/*/positions/*', inject: 'query' },
    { method: 'GET', path: '/users/*/event_positions', inject: 'query' },
    { method: 'GET', path: '/users/*/event_positions/*', inject: 'query' },
    { method: 'GET', path: '/users/*/settlement_history', inject: 'query' },
    { method: 'GET', path: '/users/*/account/pnl_statements', inject: 'query' },
    { method: 'GET', path: '/users/*/account/pnl_transactions', inject: 'query' },
    { method: 'GET', path: '/users/*/position_groups/market/*', inject: 'query' },
    { method: 'GET', path: '/users/*/position_groups/events', inject: 'query' },
    { method: 'GET', path: '/users/*/sampled_portfolio', inject: 'query' },
    { method: 'POST', path: '/users/*/events/orders', inject: 'body' },
    { method: 'POST', path: '/users/*/events/orders/*/amend', inject: 'body' },
    { method: 'POST', path: '/users/*/events/orders/*/decrease', inject: 'body' },
    { method: 'POST', path: '/users/*/orders', inject: 'body' },
    { method: 'POST', path: '/users/*/orders/*/amend', inject: 'body' },
    { method: 'POST', path: '/users/*/social/posts', inject: 'body' },
    { method: 'DELETE', path: '/users/*/events/orders/batched', inject: 'batchBody' },
    { method: 'DELETE', path: '/users/*/events/orders/*', inject: 'query' },
    // `orders/batched` must precede the `orders/*` cancel route so it wins.
    { method: 'DELETE', path: '/users/*/orders/batched', inject: 'batchBody' },
    { method: 'DELETE', path: '/users/*/orders/*', inject: 'query' },
];

/** Strips `?query` and `#frag`, and any `/trade-api/v1` or `/v1` prefix. */
function normalizePath(url: string | undefined): string {
    if (!url) return '';
    let pathname: string;
    if (url.startsWith('/')) {
        const end = url.search(/[?#]/);
        pathname = end === -1 ? url : url.slice(0, end);
    } else {
        try {
            pathname = new URL(url).pathname;
        } catch {
            // Relative, baseURL-anchored URL (e.g. axios `post('users/abc/social/posts')`).
            // Strip query/hash so it matches the same as a leading-slash path.
            const end = url.search(/[?#]/);
            pathname = end === -1 ? url : url.slice(0, end);
        }
    }
    // Patterns are leading-slash-anchored; relative URLs arrive without one.
    if (!pathname.startsWith('/')) pathname = `/${pathname}`;
    return pathname.replace(/^\/trade-api\/v1(?=\/)/, '').replace(/^\/v1(?=\/)/, '');
}

function pathMatches(pattern: string, pathname: string): boolean {
    const patternSegs = pattern.split('/');
    const pathSegs = pathname.split('/');
    if (patternSegs.length !== pathSegs.length) return false;
    for (let i = 0; i < patternSegs.length; i++) {
        const p = patternSegs[i];
        if (p === '*') {
            if (pathSegs[i].length === 0) return false;
            continue;
        }
        if (p !== pathSegs[i]) return false;
    }
    return true;
}

function findRoute(method: string | undefined, pathname: string): SubaccountRoute | undefined {
    const upper = (method ?? 'get').toUpperCase();
    return SUBACCOUNT_ROUTES.find((r) => r.method === upper && pathMatches(r.path, pathname));
}

function mergeParams(existing: unknown, subaccountNumber: number): Record<string, unknown> {
    const next: Record<string, unknown> =
        typeof existing === 'object' && existing !== null ? { ...(existing as Record<string, unknown>) } : {};
    if (next.subaccount_number === undefined) {
        next.subaccount_number = subaccountNumber;
    }
    return next;
}

function isPlainJsonObject(data: unknown): data is Record<string, unknown> {
    if (data == null || typeof data !== 'object' || Array.isArray(data)) return false;
    if (typeof FormData !== 'undefined' && data instanceof FormData) return false;
    if (typeof Blob !== 'undefined' && data instanceof Blob) return false;
    return true;
}

function injectIntoBody(data: unknown, subaccountNumber: number): unknown {
    if (!isPlainJsonObject(data)) return data;
    if (data.subaccount_number !== undefined) return data;
    return { ...data, subaccount_number: subaccountNumber };
}

function injectIntoBatchBody(data: unknown, subaccountNumber: number): unknown {
    if (!isPlainJsonObject(data) || !Array.isArray(data.orders)) return data;
    const orders = (data.orders as Array<Record<string, unknown>>).map((entry) =>
        entry && typeof entry === 'object' && entry.subaccount === undefined
            ? { ...entry, subaccount: subaccountNumber }
            : entry,
    );
    return { ...data, orders };
}

/**
 * Axios request interceptor that injects the exchange-level subaccount number
 * into the v1 endpoints listed in `SUBACCOUNT_ROUTES`. Primary (0) is a no-op —
 * omitting the field and sending 0 are equivalent on the backend, so we keep
 * requests unchanged for the default case.
 */
export function createSubaccountInterceptor(
    getSubaccountNumber: () => number,
): (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig {
    return (config) => {
        const subaccountNumber = getSubaccountNumber();
        if (subaccountNumber === 0) return config;

        const route = findRoute(config.method, normalizePath(config.url));
        if (!route) return config;

        switch (route.inject) {
            case 'query':
                config.params = mergeParams(config.params, subaccountNumber);
                return config;
            case 'body':
                config.data = injectIntoBody(config.data, subaccountNumber);
                return config;
            case 'batchBody':
                config.data = injectIntoBatchBody(config.data, subaccountNumber);
                return config;
        }
    };
}

/** Exposed for tests. Returns the matching route (if any) for a request. */
export function matchSubaccountRoute(method: string | undefined, url: string | undefined): SubaccountRoute | undefined {
    return findRoute(method, normalizePath(url ?? ''));
}
